import { CirclePartner, CustomAxiosError } from '@calm/evexia-web-sdk';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createPartnerApi from '@rhg/libs/api/create-partner-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryKeys,
    queryClient,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import {
    identifyInvitationErrorType,
    InvitationErrorType,
} from '@rhg/utils/invitations';

interface Parameters {
    dependentPartnerId: string;
    countryCode: string;
}
interface DependentPartnerResponse {
    dependentPartner: CirclePartner | null;
    dependentPartnerError: InvitationErrorType | null;
}

export const dependentPartnerByIdQueryOptions = {
    queryKey: ({ dependentPartnerId, countryCode }: Partial<Parameters>) => {
        return [
            queryKeys.dependentPartnerById,
            dependentPartnerId,
            countryCode,
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        dependentPartnerId,
        countryCode,
    }: Parameters & {
        getAccessTokenSilently: () => Promise<string>;
    }): Promise<DependentPartnerResponse> => {
        const partnerApi = createPartnerApi(getAccessTokenSilently);

        return partnerApi
            .getPartnerDependentGroupConfigByCountryCode({
                partnerId: dependentPartnerId,
                countryCode,
            })
            .then((response) => ({
                dependentPartner: response?.data ?? null,
                dependentPartnerError: null,
            }))
            .catch((e: CustomAxiosError) => {
                const errorType = identifyInvitationErrorType(e);
                if (errorType) {
                    return {
                        dependentPartner: null,
                        dependentPartnerError: errorType,
                    };
                } else {
                    throw e;
                }
            });
    },
    queryOptions: ({
        isAuthenticated,
        countryCode,
        dependentPartnerId,
    }: Partial<
        Parameters & {
            isAuthenticated: boolean;
        }
    >) => {
        return {
            enabled: !!(isAuthenticated && dependentPartnerId && countryCode),
            retry: false,
            staleTime: 15 * MINUTE_MS,
        };
    },
};

export function useDependentPartnerById({
    dependentPartnerId,
    countryCode,
}: Partial<Parameters>) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn, queryOptions } =
        dependentPartnerByIdQueryOptions;

    const options = queryOptions({
        isAuthenticated,
        dependentPartnerId,
        countryCode,
    });

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ dependentPartnerId, countryCode }),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                dependentPartnerId: dependentPartnerId!,
                countryCode: countryCode!,
            }),
        ...options,
    });
    return {
        dependentPartner: data?.dependentPartner,
        dependentPartnerError: data?.dependentPartnerError,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useGetDependentPartnerById() {
    const { getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn } = dependentPartnerByIdQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            dependentPartnerId,
            countryCode,
        }: {
            dependentPartnerId: string;
            countryCode: string;
        }) => {
            return queryFn({
                getAccessTokenSilently,
                dependentPartnerId: dependentPartnerId,
                countryCode: countryCode,
            });
        },
        onSuccess: (response, { dependentPartnerId, countryCode }) => {
            if (response.dependentPartner) {
                queryClient.setQueryData(
                    queryKey({ dependentPartnerId, countryCode }),
                    response
                );
            }
        },
    });

    return {
        getDependentPartnerById: mutate,
        getDependentPartnerByIdAsync: mutateAsync,
        ...otherProps,
    };
}
